import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import {
  Users, ArrowLeft, Plus, UserPlus, Briefcase, BarChart3,
  Star, FileText, Search, Loader2, Brain, TrendingUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = ["Engineering", "Operations", "Finance", "HR", "Marketing", "Sales", "Support"];

export default function HRHub() {
  const [members, setMembers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isAddPositionOpen, setIsAddPositionOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState("");
  const [isAssessing, setIsAssessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Form states
  const [newMember, setNewMember] = useState({ name: "", email: "", department: "", position: "", skills: "" });
  const [newPosition, setNewPosition] = useState({ title: "", department: "", level: 1, job_profile: "", required_skills: "" });
  const [reviewData, setReviewData] = useState({ member_id: "", period_start: "", period_end: "", submitted_work: "" });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, positionsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/members`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/positions`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/performance`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setMembers(membersRes.data);
      setPositions(positionsRes.data);
      setReviews(reviewsRes.data);
    } catch (err) {
      toast.error("Failed to load HR data");
    }
  };

  const addMember = async () => {
    const formData = new FormData();
    Object.entries(newMember).forEach(([k, v]) => formData.append(k, v));
    
    try {
      await axios.post(`${API}/members`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Member added");
      setIsAddMemberOpen(false);
      setNewMember({ name: "", email: "", department: "", position: "", skills: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to add member");
    }
  };

  const addPosition = async () => {
    const formData = new FormData();
    Object.entries(newPosition).forEach(([k, v]) => formData.append(k, String(v)));
    
    try {
      await axios.post(`${API}/positions`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Position added");
      setIsAddPositionOpen(false);
      setNewPosition({ title: "", department: "", level: 1, job_profile: "", required_skills: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to add position");
    }
  };

  const submitReview = async () => {
    const formData = new FormData();
    Object.entries(reviewData).forEach(([k, v]) => formData.append(k, v));
    
    try {
      const res = await axios.post(`${API}/performance/submit`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      toast.success("Review submitted");
      setIsReviewOpen(false);
      setReviewData({ member_id: "", period_start: "", period_end: "", submitted_work: "" });
      
      // Auto-assess
      setIsAssessing(true);
      try {
        await axios.post(`${API}/performance/assess/${res.data.id}`, {}, {
          headers: getHeaders(),
          withCredentials: true
        });
        toast.success("AI assessment complete");
      } catch {
        toast.error("AI assessment failed");
      }
      setIsAssessing(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to submit review");
    }
  };

  const runSkillsAudit = async () => {
    setIsAuditLoading(true);
    setAuditResult("");
    try {
      const res = await axios.post(`${API}/skills-audit`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      setAuditResult(res.data.audit);
      toast.success("Skills audit complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audit failed");
    }
    setIsAuditLoading(false);
  };

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-emerald-500";
    if (rating >= 3) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="min-h-screen bg-app">
      <TopNav />
      {/* Header */}
      <header className="border-b border-slate-200 bg-app/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span className="font-['Barlow_Condensed'] text-xl font-semibold tracking-tight">
                HR HUB
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-8000" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-300 rounded-none w-48"
                data-testid="search-members-input"
              />
            </div>

            <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300 rounded-none" data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 rounded-none max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed']">Add New Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Full name"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="member-name-input"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="member-email-input"
                  />
                  <Select value={newMember.department} onValueChange={(v) => setNewMember({ ...newMember, department: v })}>
                    <SelectTrigger className="bg-app border-slate-300 rounded-none" data-testid="member-department-select">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300 rounded-none">
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Position"
                    value={newMember.position}
                    onChange={(e) => setNewMember({ ...newMember, position: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="member-position-input"
                  />
                  <Input
                    placeholder="Skills (comma separated)"
                    value={newMember.skills}
                    onChange={(e) => setNewMember({ ...newMember, skills: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="member-skills-input"
                  />
                  <Button className="btn-primary rounded-none w-full" onClick={addMember} data-testid="save-member-btn">
                    Add Member
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isAddPositionOpen} onOpenChange={setIsAddPositionOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-300 rounded-none" data-testid="add-position-btn">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Add Position
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 rounded-none max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed']">Add New Position</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Title"
                    value={newPosition.title}
                    onChange={(e) => setNewPosition({ ...newPosition, title: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="position-title-input"
                  />
                  <Select value={newPosition.department} onValueChange={(v) => setNewPosition({ ...newPosition, department: v })}>
                    <SelectTrigger className="bg-app border-slate-300 rounded-none" data-testid="position-department-select">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300 rounded-none">
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Level (1-10)"
                    type="number"
                    min={1}
                    max={10}
                    value={newPosition.level}
                    onChange={(e) => setNewPosition({ ...newPosition, level: parseInt(e.target.value) })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="position-level-input"
                  />
                  <Textarea
                    placeholder="Job profile description"
                    value={newPosition.job_profile}
                    onChange={(e) => setNewPosition({ ...newPosition, job_profile: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="position-profile-input"
                  />
                  <Input
                    placeholder="Required skills (comma separated)"
                    value={newPosition.required_skills}
                    onChange={(e) => setNewPosition({ ...newPosition, required_skills: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="position-skills-input"
                  />
                  <Button className="btn-primary rounded-none w-full" onClick={addPosition} data-testid="save-position-btn">
                    Add Position
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary rounded-none" data-testid="submit-review-btn">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Submit Review
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 rounded-none max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed']">Submit Performance Review</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Select value={reviewData.member_id} onValueChange={(v) => setReviewData({ ...reviewData, member_id: v })}>
                    <SelectTrigger className="bg-app border-slate-300 rounded-none" data-testid="review-member-select">
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300 rounded-none">
                      {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Period Start</Label>
                      <Input
                        type="date"
                        value={reviewData.period_start}
                        onChange={(e) => setReviewData({ ...reviewData, period_start: e.target.value })}
                        className="bg-app border-slate-300 rounded-none"
                        data-testid="review-start-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Period End</Label>
                      <Input
                        type="date"
                        value={reviewData.period_end}
                        onChange={(e) => setReviewData({ ...reviewData, period_end: e.target.value })}
                        className="bg-app border-slate-300 rounded-none"
                        data-testid="review-end-input"
                      />
                    </div>
                  </div>
                  <Textarea
                    placeholder="Describe work completed during this period..."
                    value={reviewData.submitted_work}
                    onChange={(e) => setReviewData({ ...reviewData, submitted_work: e.target.value })}
                    className="bg-app border-slate-300 rounded-none min-h-[150px]"
                    data-testid="review-work-input"
                  />
                  <Button 
                    className="btn-primary rounded-none w-full" 
                    onClick={submitReview}
                    disabled={isAssessing}
                    data-testid="submit-review-confirm-btn"
                  >
                    {isAssessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        AI Assessing...
                      </>
                    ) : (
                      <>Submit & AI Assess</>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="bg-app border border-slate-200 rounded-none p-1 mb-6">
            <TabsTrigger value="members" className="rounded-none data-[state=active]:bg-slate-800" data-testid="tab-members">
              <Users className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger value="positions" className="rounded-none data-[state=active]:bg-slate-800" data-testid="tab-positions">
              <Briefcase className="w-4 h-4 mr-2" />
              Positions
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none data-[state=active]:bg-slate-800" data-testid="tab-reviews">
              <BarChart3 className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-slate-800" data-testid="tab-audit">
              <Brain className="w-4 h-4 mr-2" />
              Skills Audit
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMembers.map((member) => (
                <div key={member.id} className="member-card" data-testid={`member-card-${member.id}`}>
                  <div className="flex items-start gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={member.avatar} />
                      <AvatarFallback className="bg-emerald-600 text-white">
                        {member.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                        {member.name}
                      </h3>
                      <p className="text-sm text-slate-600">{member.position}</p>
                      <span className="badge badge-emerald mt-2">{member.department}</span>
                    </div>
                  </div>
                  {member.skills?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {member.skills.slice(0, 4).map((skill) => (
                        <span key={skill} className="text-xs bg-slate-800 px-2 py-1 text-slate-600">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {filteredMembers.length === 0 && (
                <p className="col-span-full text-center text-slate-8000 py-12">
                  No members found. Add your first team member!
                </p>
              )}
            </div>
          </TabsContent>

          {/* Positions Tab */}
          <TabsContent value="positions">
            <div className="grid md:grid-cols-2 gap-4">
              {positions.map((position) => (
                <div key={position.id} className="bg-white border border-slate-200 p-6" data-testid={`position-card-${position.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">
                        {position.title}
                      </h3>
                      <span className="badge badge-emerald mt-1">{position.department}</span>
                    </div>
                    <span className="text-sm text-slate-8000 font-['JetBrains_Mono']">
                      Level {position.level}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{position.job_profile}</p>
                  {position.required_skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {position.required_skills.map((skill) => (
                        <span key={skill} className="text-xs bg-slate-800 px-2 py-1 text-slate-600">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {positions.length === 0 && (
                <p className="col-span-full text-center text-slate-8000 py-12">
                  No positions defined. Add positions to build your org structure!
                </p>
              )}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-4">
              {reviews.map((review) => {
                const member = members.find(m => m.id === review.member_id);
                return (
                  <div key={review.id} className="bg-white border border-slate-200 p-6" data-testid={`review-card-${review.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800">
                          {member?.name || "Unknown Member"}
                        </h3>
                        <p className="text-sm text-slate-8000 font-['JetBrains_Mono']">
                          {review.period_start} → {review.period_end}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {review.overall_rating && (
                          <div className="flex items-center gap-1">
                            <Star className={`w-5 h-5 ${getRatingColor(review.overall_rating)}`} />
                            <span className={`font-['JetBrains_Mono'] font-semibold ${getRatingColor(review.overall_rating)}`}>
                              {review.overall_rating.toFixed(1)}
                            </span>
                          </div>
                        )}
                        <span className={`badge ${
                          review.status === "ai_reviewed" ? "badge-blue" :
                          review.status === "manager_reviewed" ? "badge-green" :
                          "badge-amber"
                        }`}>
                          {review.status.replace("_", " ")}
                        </span>
                      </div>
                    </div>
                    
                    {review.ai_assessment && (
                      <div className="bg-app border border-slate-200 p-4 mt-4">
                        <p className="text-xs text-blue-400 uppercase tracking-wider mb-2">AI Assessment</p>
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap font-['IBM_Plex_Sans']">
                          {review.ai_assessment}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
              {reviews.length === 0 && (
                <p className="text-center text-slate-8000 py-12">
                  No performance reviews yet. Submit a review to get AI-powered assessment!
                </p>
              )}
            </div>
          </TabsContent>

          {/* Skills Audit Tab */}
          <TabsContent value="audit">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-semibold text-slate-800">
                    AI Skills Audit
                  </h2>
                  <p className="text-slate-600">
                    Analyze skills gaps across your organization
                  </p>
                </div>
                <Button 
                  className="btn-primary rounded-none"
                  onClick={runSkillsAudit}
                  disabled={isAuditLoading || members.length === 0}
                  data-testid="run-audit-btn"
                >
                  {isAuditLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Run Skills Audit
                    </>
                  )}
                </Button>
              </div>

              {auditResult ? (
                <div className="bg-white border border-slate-200 p-6">
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-['IBM_Plex_Sans'] leading-relaxed">
                    {auditResult}
                  </pre>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 p-12 text-center">
                  <Brain className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                  <h3 className="font-['Barlow_Condensed'] text-xl text-slate-600 mb-2">
                    Ready to Audit
                  </h3>
                  <p className="text-slate-8000 max-w-md mx-auto">
                    Add members and positions with their skills, then run the AI audit 
                    to identify skill gaps and training priorities.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
